import type { FormulaFunctions } from '@pst/shared';
import type { CostItem, CostPlan, CostStage, CostVariantResult } from '../../api';

/** The result of one variant; the primary one if the id is unknown. */
export function resultFor(plan: CostPlan, variantId: string): CostVariantResult {
  return plan.results.find((r) => r.variantId === variantId) ?? plan.results.find((r) => r.variantId === plan.primaryVariantId) ?? plan.results[0]!;
}

/** Values of a variant by name, for previews while typing a formula. */
export function variablesOf(result: CostVariantResult, stage: CostStage): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [name, v] of Object.entries(result.values)) if (v.value !== null) out[name] = v.value;
  for (const [name, v] of Object.entries(result.ratios[stage] ?? {})) if (v.value !== null) out[name] = v.value;
  const t = result.totals[stage];
  out.NETTO = t?.net ?? 0;
  out.GESAMT = t?.withRisk ?? 0;
  return out;
}

const end = (x: number): number => (x % 100 === 0 ? x + 99 : x % 10 === 0 ? x + 9 : x);

/**
 * KG(300), KG(200;600) for previews: the rolled-up totals of the stage. The
 * server's calculation leaves the line itself out; a preview need not.
 */
export function kgFunctions(result: CostVariantResult, stage: CostStage): FormulaFunctions {
  return {
    KG: (args) => {
      const [a, b] = args;
      if (a === undefined) throw new Error('KG() braucht eine Kostengruppe');
      const lo = a;
      const hi = end(b ?? a);
      const inside = (code: string | null): boolean => code !== null && Number(code.slice(0, 3)) >= lo && Number(code.slice(0, 3)) <= hi;
      return result.rollup.groups.filter((g) => inside(g.code) && !inside(g.parent)).reduce((s, g) => s + (g.stages[stage] ?? 0), 0);
    },
  };
}

/** Whether a line counts in a variant: shared lines count everywhere. */
export const inVariant = (item: CostItem, variantId: string): boolean => item.variantId === null || variantId === '' || item.variantId === variantId;

/** Remembers the chosen variant per project, where the browser lets us. */
export function storedVariant(projectId: string): string | null {
  try {
    return window.localStorage.getItem(`pst.costs.variant.${projectId}`);
  } catch {
    return null;
  }
}

export function storeVariant(projectId: string, variantId: string): void {
  try {
    window.localStorage.setItem(`pst.costs.variant.${projectId}`, variantId);
  } catch {
    // Private windows may refuse; the choice then lasts until reload.
  }
}
